/**
 * Typed EFEM API facade over the generated contract client.
 */

import * as schemas from '@cnapse/shared/schemas.js';
import type {
  AcquireEfemLockResponse,
  BaseResponse,
  BatchWaferTransferEfemRequest,
  BatchWaferTransferEfemResponse,
  EfemClientAuthenticationResultResponseData,
  EfemClientStatusData,
  EfemConfig,
  EfemGatewayHardwareProfile,
  EfemLoadPortBindingView,
  EfemLockOperation,
  EfemLockStatusResponse,
  EfemStatusResponseData,
  ExtendEfemLockResponse,
  GetEfemClientsResponseData,
  GetEfemClientsWithLocksResponse,
  HandheldBindPreviewEntry,
  LoadPortsLoadUnloadResponseData,
  LoadPortsResponseData,
  ReleaseEfemLockResponse,
  SlotMapResponseData,
  TenantEfemConfigResponseData,
  WaferImageCollectionResponseData,
  WaferTransferEfemRequest,
  EfemClientConfig,
} from '@cnapse/shared/schemas.js';
import { EfemPeripheralMatrixResponseSchema } from '@cnapse/shared/schemas.js';
import { z } from 'zod';
import { efemApi } from '../api/generated/efem.api.js';

export type EfemHardwareProfileData = {
  efemClientId: number;
  gatewaySerialNumber: string | null;
  gatewayName: string | null;
  hardwareProfile: EfemGatewayHardwareProfile | null;
  configured: boolean;
};

class EfemService {
  async getEfemStatus(): Promise<EfemStatusResponseData> {
    return efemApi.getStatus() as Promise<EfemStatusResponseData>;
  }

  async authenticateEfemClients(): Promise<EfemClientAuthenticationResultResponseData> {
    return efemApi.authenticateClients() as Promise<EfemClientAuthenticationResultResponseData>;
  }

  async getEfemClientStatus(gatewaySerialNumber: string): Promise<EfemClientStatusData> {
    return efemApi.getClientStatus(gatewaySerialNumber) as Promise<EfemClientStatusData>;
  }

  async getEfemLoadPorts(gatewaySerialNumber: string): Promise<LoadPortsResponseData> {
    return efemApi.getLoadPorts(gatewaySerialNumber) as Promise<LoadPortsResponseData>;
  }

  async postEfemScanContainer(
    gatewaySerialNumber: string,
    loadPortId: number,
    timeoutMs?: number,
  ): Promise<{ scannerSerialNumber: string; binding: EfemLoadPortBindingView | null }> {
    return efemApi.scanContainer(
      { gatewaySerialNumber, loadPortId },
      { loadPortId, timeoutMs },
    ) as Promise<{ scannerSerialNumber: string; binding: EfemLoadPortBindingView | null }>;
  }

  async patchEfemLoadPortBinding(
    gatewaySerialNumber: string,
    loadPortId: number,
    body: { containerId?: number; clear?: boolean; forceRebind?: boolean },
  ): Promise<EfemLoadPortBindingView> {
    return efemApi.patchLoadPortBinding({ gatewaySerialNumber, loadPortId }, body) as Promise<EfemLoadPortBindingView>;
  }

  async armHandheldBindLoadPort(
    gatewaySerialNumber: string,
    loadPortId: number,
  ): Promise<{ armedLoadPortId: number | null; previews: HandheldBindPreviewEntry[] }> {
    return efemApi.armHandheldBind(gatewaySerialNumber, { loadPortId }) as Promise<{
      armedLoadPortId: number | null;
      previews: HandheldBindPreviewEntry[];
    }>;
  }

  async getHandheldBindPreview(
    gatewaySerialNumber: string,
  ): Promise<{ armedLoadPortId: number | null; previews: HandheldBindPreviewEntry[] }> {
    return efemApi.getHandheldBindPreview(gatewaySerialNumber) as Promise<{
      armedLoadPortId: number | null;
      previews: HandheldBindPreviewEntry[];
    }>;
  }

  async confirmHandheldBind(
    gatewaySerialNumber: string,
    forceRebind?: boolean,
  ): Promise<EfemLoadPortBindingView[]> {
    return efemApi.confirmHandheldBind(gatewaySerialNumber, { forceRebind: forceRebind ?? false }) as Promise<
      EfemLoadPortBindingView[]
    >;
  }

  async clearHandheldBindSession(gatewaySerialNumber: string): Promise<void> {
    await efemApi.clearHandheldBindSession(gatewaySerialNumber);
  }

  async loadEfemContainers(
    gatewaySerialNumber: string,
    loadPortIds: number[],
  ): Promise<LoadPortsLoadUnloadResponseData[]> {
    return efemApi.loadContainers(gatewaySerialNumber, { loadPortIds }) as Promise<LoadPortsLoadUnloadResponseData[]>;
  }

  async unloadEfemContainers(
    gatewaySerialNumber: string,
    loadPortIds: number[],
  ): Promise<LoadPortsLoadUnloadResponseData[]> {
    return efemApi.unloadContainers(gatewaySerialNumber, { loadPortIds }) as Promise<LoadPortsLoadUnloadResponseData[]>;
  }

  async getEfemSlotMap(gatewaySerialNumber: string, loadPortId: number): Promise<SlotMapResponseData> {
    return efemApi.getSlotMap({ gatewaySerialNumber, loadPortId }) as Promise<SlotMapResponseData>;
  }

  async collectWaferImages(
    gatewaySerialNumber: string,
    loadPortId: number,
    slotNumbers: number[],
  ): Promise<WaferImageCollectionResponseData> {
    return efemApi.collectWaferImages(gatewaySerialNumber, { loadPortId, slotNumbers }) as Promise<WaferImageCollectionResponseData>;
  }

  async acquireWaferIdAtStation(
    gatewaySerialNumber: string,
    body: schemas.WaferIdStationAcquireRequest,
  ): Promise<schemas.WaferIdStationAcquireResponseData> {
    return efemApi.acquireWaferIdAtStation(gatewaySerialNumber, body) as Promise<schemas.WaferIdStationAcquireResponseData>;
  }

  async saveWaferIdStationAcquire(
    body: schemas.WaferIdStationAcquireSaveRequest,
  ): Promise<schemas.WaferIdStationAcquireSaveResponseData> {
    return efemApi.saveWaferIdStationAcquire(body) as Promise<schemas.WaferIdStationAcquireSaveResponseData>;
  }

  async transferWaferEfem(
    gatewaySerialNumber: string,
    request: WaferTransferEfemRequest,
  ): Promise<BatchWaferTransferEfemResponse> {
    return efemApi.transferWafer(gatewaySerialNumber, request) as Promise<BatchWaferTransferEfemResponse>;
  }

  async batchTransferWafersEfem(
    gatewaySerialNumber: string,
    request: BatchWaferTransferEfemRequest,
  ): Promise<BatchWaferTransferEfemResponse> {
    return efemApi.batchTransferWafers(gatewaySerialNumber, request) as Promise<BatchWaferTransferEfemResponse>;
  }

  async verifyTransfer(data: {
    sourceScans: Array<{ slotNumber: number; fingerprint: string; imageId?: number }>;
    targetScans: Array<{ slotNumber: number; fingerprint: string; imageId?: number }>;
    transferId?: string;
    notes?: string;
  }): Promise<{
    verified: boolean;
    matches: Array<{ fingerprint: string; sourceSlot: number; targetSlot: number; verified: boolean }>;
    missingInTarget: Array<{ fingerprint: string; sourceSlot: number }>;
    extraInTarget: Array<{ fingerprint: string; targetSlot: number }>;
    discrepancies: Array<{ type: string; message: string }>;
  }> {
    return efemApi.verifyTransfer(data) as Promise<{
      verified: boolean;
      matches: Array<{ fingerprint: string; sourceSlot: number; targetSlot: number; verified: boolean }>;
      missingInTarget: Array<{ fingerprint: string; sourceSlot: number }>;
      extraInTarget: Array<{ fingerprint: string; targetSlot: number }>;
      discrepancies: Array<{ type: string; message: string }>;
    }>;
  }

  async getEfemConfig(): Promise<TenantEfemConfigResponseData> {
    return efemApi.getConfig() as Promise<TenantEfemConfigResponseData>;
  }

  async initializeEfemConfig(config: { maxClients: number; clients: EfemClientConfig[] }): Promise<void> {
    await efemApi.initializeConfig(config);
  }

  async updateEfemConfig(config: Partial<EfemConfig>): Promise<void> {
    await efemApi.updateConfig(config);
  }

  async getEfemClients(): Promise<GetEfemClientsResponseData> {
    const response = await efemApi.listClients() as GetEfemClientsWithLocksResponse;
    return response.data as unknown as GetEfemClientsResponseData;
  }

  async createEfemClient(clientData: {
    name: string;
    serialNumber?: string;
  }): Promise<GetEfemClientsResponseData> {
    return efemApi.createClient(clientData) as Promise<GetEfemClientsResponseData>;
  }

  async updateEfemClient(
    gatewaySerialNumber: string,
    clientData: { name?: string; imagesPerWafer?: number; isActive?: boolean },
  ): Promise<GetEfemClientsResponseData> {
    return efemApi.updateClient(gatewaySerialNumber, clientData) as Promise<GetEfemClientsResponseData>;
  }

  async acquireEfemLock(
    gatewaySerialNumber: string,
    operation: EfemLockOperation,
  ): Promise<AcquireEfemLockResponse> {
    return efemApi.acquireLock(gatewaySerialNumber, { operation }) as Promise<AcquireEfemLockResponse>;
  }

  async releaseEfemLock(gatewaySerialNumber: string): Promise<ReleaseEfemLockResponse> {
    return efemApi.releaseLock(gatewaySerialNumber) as Promise<ReleaseEfemLockResponse>;
  }

  async extendEfemLock(gatewaySerialNumber: string): Promise<ExtendEfemLockResponse> {
    return efemApi.extendLock(gatewaySerialNumber) as Promise<ExtendEfemLockResponse>;
  }

  async getEfemLockStatus(gatewaySerialNumber: string): Promise<EfemLockStatusResponse> {
    return efemApi.getLockStatus(gatewaySerialNumber) as Promise<EfemLockStatusResponse>;
  }

  async getEfemClientsWithLocks(): Promise<GetEfemClientsWithLocksResponse> {
    return efemApi.listClients() as Promise<GetEfemClientsWithLocksResponse>;
  }

  async forceReleaseEfemLock(gatewaySerialNumber: string): Promise<BaseResponse> {
    return efemApi.forceReleaseLock(gatewaySerialNumber) as Promise<BaseResponse>;
  }

  async getEfemHardwareProfile(gatewaySerialNumber: string): Promise<EfemHardwareProfileData> {
    return efemApi.getHardwareProfile({ gatewaySerialNumber }) as Promise<EfemHardwareProfileData>;
  }

  async putEfemHardwareProfile(
    gatewaySerialNumber: string,
    profile: EfemGatewayHardwareProfile,
  ): Promise<EfemHardwareProfileData> {
    return efemApi.putHardwareProfile({ gatewaySerialNumber }, profile) as Promise<EfemHardwareProfileData>;
  }

  async getEfemPeripheralMatrix(
    gatewaySerialNumber: string,
  ): Promise<z.infer<typeof EfemPeripheralMatrixResponseSchema>['data']> {
    return efemApi.getPeripheralMatrix({ gatewaySerialNumber }) as Promise<
      z.infer<typeof EfemPeripheralMatrixResponseSchema>['data']
    >;
  }
}

export const efemService = new EfemService();
export { EfemService };
